import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchTerms = ({ sections, onSearchResult }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query?.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      onSearchResult(null);
      return;
    }

    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      const results = [];
      const searchTerm = query?.toLowerCase();
      
      sections?.forEach((section) => {
        // Search in section title
        if (section?.title?.toLowerCase()?.includes(searchTerm)) {
          results?.push({
            sectionId: section?.id,
            sectionTitle: section?.title,
            type: 'title',
            match: section?.title
          });
        }
        
        // Search in section content
        section?.content?.forEach((item, itemIndex) => {
          if (item?.type === 'paragraph' && item?.text?.toLowerCase()?.includes(searchTerm)) {
            results?.push({
              sectionId: section?.id,
              sectionTitle: section?.title,
              type: 'content',
              match: item?.text?.substring(0, 100) + '...'
            });
          }
          
          if (item?.type === 'list' && item?.items) {
            item?.items?.forEach((listItem) => {
              if (listItem?.toLowerCase()?.includes(searchTerm)) {
                results?.push({
                  sectionId: section?.id,
                  sectionTitle: section?.title,
                  type: 'list',
                  match: listItem
                });
              }
            });
          }
        });
      });
      
      setSearchResults(results?.slice(0, 5)); // Limit to 5 results
      setIsSearching(false);
      onSearchResult(results?.length > 0 ? results?.[0]?.sectionId : null);
    }, 300);
  };

  const scrollToResult = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element?.getBoundingClientRect()?.top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          type="search"
          placeholder="Search terms and conditions..."
          value={searchQuery}
          onChange={(e) => handleSearch(e?.target?.value)}
          className="pl-10 pr-4"
        />
        <Icon 
          name={isSearching ? "Loader2" : "Search"} 
          size={18} 
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary ${
            isSearching ? 'animate-spin' : ''
          }`}
        />
      </div>
      {/* Search Results */}
      {searchResults?.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg liquid-shadow z-50 max-h-64 overflow-y-auto">
          <div className="p-2">
            <p className="text-xs text-text-secondary px-3 py-2 font-medium">
              Found {searchResults?.length} result{searchResults?.length !== 1 ? 's' : ''}
            </p>
            {searchResults?.map((result, index) => (
              <button
                key={index}
                onClick={() => scrollToResult(result?.sectionId)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted brand-transition"
              >
                <div className="flex items-start space-x-2">
                  <Icon 
                    name={result?.type === 'title' ? 'Hash' : result?.type === 'list' ? 'List' : 'FileText'} 
                    size={14} 
                    className="text-primary mt-1 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">
                      {result?.sectionTitle}
                    </p>
                    <p className="text-xs text-text-secondary line-clamp-2">
                      {result?.match}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* No Results */}
      {searchQuery && !isSearching && searchResults?.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg liquid-shadow z-50">
          <div className="p-4 text-center">
            <Icon name="SearchX" size={24} className="text-text-secondary mx-auto mb-2" />
            <p className="text-sm text-text-secondary">No results found for "{searchQuery}"</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchTerms;